import React, { useState, useCallback, useEffect } from "react";
import "./ResultsComponent.css";

//import components used on page
import StickersBox from "components/StickerBox/StickerBox"; // Ensure the import path is correct
import DownloadButton from "components/DownloadButton/DownloadButton";
import Gallery from "components/Gallery/Gallery";

//import images used on page
import GustavusLogo from "assets/logos/gustavus_reg_mark_dark.svg";
import GIcon from "assets/icons/g_logo_frame.svg";
import polardLabel from "assets/miscellaneous/gustavus_edu_polaroid_label.svg";

//import all the stickers
import art from "assets/stickers/Sticker_art.svg";
import gLogo from "assets/stickers/Sticker_G.svg";
import gaLogo from "assets/stickers/Sticker_GA.svg";
import helmet from "assets/stickers/Sticker_Helmet.svg";
import roar from "assets/stickers/Sticker_ROAR.svg";
import classYear from "assets/stickers/Sticker_class-of-2028.svg";
import finger from "assets/stickers/Sticker_foam-hand.svg";
import lion from "assets/stickers/Sticker_lion-head.svg";
import lionPaw from "assets/stickers/Sticker_lion-paw.svg";
import math from "assets/stickers/Sticker_math.svg";
import music from "assets/stickers/Sticker_music.svg";
import pennant from "assets/stickers/Sticker_pennant.svg";
import photography from "assets/stickers/Sticker_photography.svg";
import science from "assets/stickers/Sticker_science.svg";
import seal from "assets/stickers/Sticker_seal.svg";
import shield from "assets/stickers/Sticker_shield.svg";
import tagline from "assets/stickers/Sticker_tagline.svg";
import theator from "assets/stickers/Sticker_theator.svg";
import { ReactComponent as CloseSymbol } from "assets/icons/close-symbol.svg";

const ResultsComponent = ({ setAppBackgroundColor, openAIResponse, onNewStart}) => {
  const [stickersOnImage, setStickersOnImage] = useState([]); // Track stickers placed on the image
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);


  // Initialize available stickers here
  const availableStickers = [
    { id: "sticker1", src: art },
    { id: "sticker2", src: roar },
    { id: "sticker3", src: finger },
    { id: "sticker4", src: gaLogo },
    { id: "sticker5", src: lion },
    { id: "sticker6", src: seal },
    { id: "sticker7", src: shield },
    { id: "sticker8", src: gLogo },
    { id: "sticker9", src: helmet },
    { id: "sticker10", src: classYear },
    { id: "sticker11", src: lionPaw },
    { id: "sticker12", src: math },
    { id: "sticker13", src: music },
    { id: "sticker14", src: pennant },
    { id: "sticker15", src: photography },
    { id: "sticker16", src: science },
    { id: "sticker17", src: tagline },
    { id: "sticker18", src: theator },
  ];

  //* callback for when the user selects the sticker from the modal
  const onStickerSelect = (stickerSrc) => {
    setStickersOnImage([
      ...stickersOnImage,
      { src: stickerSrc, x: 100, y: 100, id: Math.random() },
    ]);
  };

  // Update the position of a sticker
  const moveSticker = useCallback((index, dx, dy) => {
    setStickersOnImage((stickers) =>
      stickers.map((sticker, i) =>
        i === index
          ? { ...sticker, x: sticker.x + dx, y: sticker.y + dy }
          : sticker
      )
    );
  }, []);

  // * handle the start of the dragging process
  const startDrag = (index, event) => {
    event.preventDefault();
    // Normalize touch and mouse coordinates
    const getCoords = (evt) => ({
      x: evt.clientX || evt.touches[0].clientX,
      y: evt.clientY || evt.touches[0].clientY,
    });

    let startPos = getCoords(event);

    // * handle moves as the sticker is dragged
    const onMove = (moveEvent) => {
      let currentPos = getCoords(moveEvent);
      moveSticker(index, currentPos.x - startPos.x, currentPos.y - startPos.y);
      startPos = currentPos;
    };

    // Clean up function
    const endDrag = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", endDrag);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchmove", onMove);
    document.addEventListener("touchend", endDrag);
  };

  useEffect(() => {
    // This code will run whenever openAIResponse changes
    if (openAIResponse) {
      setAppBackgroundColor("primary-blue");
    }
  }, [openAIResponse]);

  return openAIResponse && openAIResponse.image ? (
    <div className="results">
      <div className="results-header">
        <h1>You look good as a Gustie.</h1>
        <p>As a Gustie, you can be yourself and live your whole life.</p>
      </div>
      <div className="column-container">
        <div className="left-column">
          <div className="polaroid-container">
            <div className="image-container">
              <img className="polaroid-label" src={polardLabel}></img>
              <div className="photo-area">
                <img
                  className="generated-image"
                  src={`data:image/jpeg;base64,${openAIResponse.image}`}
                  alt="Generated"
                />
                <img className="icon" src={GIcon} />
                <img
                  className="logo"
                  style={{ width: "33%", float: "left" }}
                  src={GustavusLogo}
                />

                {/* TODO render these stickers as draggable components */}
                {stickersOnImage.map((sticker, index) => (
                  <img
                    key={sticker.id}
                    src={sticker.src}
                    className="sticker"
                    style={{ left: `${sticker.x}px`, top: `${sticker.y}px` }}
                    alt="Sticker"
                    onMouseDown={(e) => startDrag(index, e)}
                    onTouchStart={(e) => startDrag(index, e)}
                  />
                ))}
              </div>
            </div>
          </div>
          <StickersBox
            initialStickers={availableStickers}
            onStickerSelect={onStickerSelect}
          />
        </div>
        <div className="right-column">
          <div className="right-column-content">
            <p className="tagline">
              <span>Download and share</span> your image using <span>#GustieBot.</span>
            </p>

            <div className="buttons-container">
              <DownloadButton
                baseImageSrc={`data:image/jpeg;base64,${openAIResponse.image}`}
                stickers={stickersOnImage}
              />
              <a
                className="button secondary data-gtm-createNew"
                onClick={onNewStart}
              >
                Start a new design
              </a>
              <a className="button secondary data-gtm-learnMore" href="https://gustavus.edu/">
                More about Gustavus
              </a>
            </div>
            <div className="horizontal-line"></div>
            <p className="disclaimer">
              Curious how GustieBot came to be?&nbsp;
              <a href="#" onClick={openModal} className="learn-more-link data-gtm-discoverAI">
                Discover how we're using and questioning AI on campus.
              </a>
            </p>

            {modalIsOpen && (
            <div className="modal-overlay" onClick={closeModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button onClick={closeModal} className="close-button">
                <CloseSymbol />
              </button>
                <div className="tooltip-text">
                  <h3>AI AT GUSTAVUS</h3>
                  <p>Everywhere, people are questioning the implications of AI and trying to figure out how to use this new tool in helpful ways.</p>
                  <p>At Gustavus, our intelligence is not artificial. Our students and faculty are already using AI in classes and for projects. They are also discussing potential problems with AI, including what sources AI uses to learn and how accurately AI images represent ethnicity.</p>
                  <h3>BEHIND THE BOT</h3>
                  <p>We had the idea to use an AI-assisted image generator to share the Gustavus story. Gusties can have multiple roles, in one place, at the same time: Why be a biologist, when you can be a biologist-trumpeter-kayaker-entrepreneur-super fan-world traveler?</p>
                  <p>And so, the GustieBot asks a series of questions about interests and identities that are fed into an image generator, with coded “guardrails” to produce a fun, “do it all” image that (hopefully) doesn’t offend.</p>
                  <h3>NEXT</h3>
                  <p>As AI expands, we wonder how the GustieBot might adapt - making a video, for example. Do the guardrails work? Using the bot, can people see themselves at Gustavus and be optimistic about the future?</p>
                  <p>As we share the GustieBot with prospective students and other people, we're glad to get your feedback or questions. Email us at <a href="mailto:admission@gustavus.edu">admission@gustavus.edu</a>. To learn more about Gustavus, go to <a href="https://gustavus.edu/go/getmore/" target="_blank">https://gustavus.edu/go/getmore/</a>.</p>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
      <Gallery stickers={availableStickers}></Gallery>
    </div >
  ) : null;
};

export default ResultsComponent;
